import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as _ from 'underscore';
import { GameStateDB } from '../../../data/datatypes';
import { getGameState, saveGameState } from '../../../services/DBService/DbService';
import { useAutoSaveOnPageLeave, useParentContainerDisableScrolling } from '../../../store/hooks';
import TextWithReferences from '../PageElements/TextWithReferences/TextWithReferences';
import { SectionNavBarButtonOptions } from '../../atoms/SectionNavButton.types';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import ApproachAvoidCard from './ApproachAvoidCard';
import GameImageSlider from './GameImageSlider';
import { GameApproachAvoidData, GameApproachAvoidState } from '../Games.types';

type Properties = {
    data: GameApproachAvoidData;
    setNavBarLeftButton: (options: SectionNavBarButtonOptions | undefined) => void;
    setNavBarRightButton: (options: SectionNavBarButtonOptions | undefined) => void;
};

const ApproachAvoid = (props: Properties) => {
    const { startCaption, startDesc, selectedCaption, selectedDesc, unselectedCaption, unselectedDesc, swipeUpDesc, swipeDownDesc, cards } = props.data;
    const { step } = useParams();       // start, swiping, selected, unselected
    const navigate = useNavigate();
    const path = useLocation().pathname;

    const [currentCardIndex, setCurrentCardIndex] = React.useState<number>(0);
    const [cardsSwipedUp, setCardsSwipedUp] = React.useState<(boolean | null)[]>(new Array(cards.length).fill(null));   // null: card was not swiped up/down yet

    const getDbId = React.useCallback((): `game_${string}` => {
        const splittedPath = path.split('/');
        let correctPath: string;
        if (splittedPath[splittedPath.length - 1].startsWith('item_')) {
            correctPath = splittedPath.join('/');
        } else {    // leave last part of the path out (/start, /swiping, /selected, ...)
            correctPath = splittedPath.slice(0, -1).join('/');
        }
        return `game_${getUrlWithTransientStateProperties(correctPath)}`;
    }, [path]);

    useParentContainerDisableScrolling();

    useAutoSaveOnPageLeave(
        [cardsSwipedUp, getDbId],
        async () => {
            const gameState: GameStateDB = {
                _id: getDbId(),
                state: {
                    cardsSwipedUp,
                }
            };
            await saveGameState(gameState);
        },
        [],
        async () => {
            const state = await getGameState(getDbId());
            if (state) {
                setCardsSwipedUp((state.state as GameApproachAvoidState).cardsSwipedUp);
            }
        }
    );

    React.useEffect(() => {
        if (!step) {    // redirect to start
            navigate('start', { replace: true });
        } else {        // set navigation buttons based on current step
            switch (step) {
                case 'start':
                    props.setNavBarLeftButton(undefined);
                    props.setNavBarRightButton({
                        text: 'Los gehts!',
                        onClick: () => navigate('../swiping')
                    });
                    break;
                case 'swiping':
                    props.setNavBarLeftButton({
                        text: 'Zurück',
                        onClick: () => navigate('../start')
                    });
                    props.setNavBarRightButton({
                        text: 'Weiter',
                        onClick: () => navigate('../selected'),
                        disabled: cardsSwipedUp.some(card => card === null)
                    });
                    break;
                case 'selected':
                    props.setNavBarLeftButton({
                        text: 'Zurück',
                        onClick: restart
                    });
                    props.setNavBarRightButton({
                        text: 'Weiter',
                        onClick: () => navigate('../unselected'),
                    });
                    break;
                case 'unselected':
                    props.setNavBarLeftButton({
                        text: 'Zurück',
                        onClick: () => navigate('../selected')
                    });
                    props.setNavBarRightButton(undefined);
                    break;
                default:
            }
        }
    }, [step]);

    // unset custom nav-bar-buttons on unload
    React.useEffect(() => {
        return () => {
            props.setNavBarLeftButton(undefined);
            props.setNavBarRightButton(undefined);
        };
    }, []);

    function swiped(up: boolean) {
        let newCardsSwipedUp = [...cardsSwipedUp];
        if (up) {
            newCardsSwipedUp[currentCardIndex] = true;
        } else {
            newCardsSwipedUp[currentCardIndex] = false;
        }
        setCardsSwipedUp(newCardsSwipedUp);

        if (currentCardIndex >= cards.length - 1) {
            navigate('../selected');
        } else {
            setCurrentCardIndex(currentCardIndex + 1);
        }
    }

    function restart() {
        setCurrentCardIndex(0);
        navigate('../swiping');
    }

    let content: JSX.Element | JSX.Element[];

    switch (step) {
        case 'start':
            content = <>
                <h1>{startCaption}</h1>
                <div>
                    <TextWithReferences text={startDesc || ''} />
                </div>
            </>;
            break;

        case 'swiping':
            const currentCard = cards[currentCardIndex];
            content = (
                <GameImageSlider
                    text={currentCard.text}
                    imageId={currentCard.imageId}
                    swipeUpLabel={swipeUpDesc}
                    swipeDownLabel={swipeDownDesc}
                    swipedUp={swiped.bind(this, true)}
                    swipedDown={swiped.bind(this, false)}
                />);
            break;
        case 'selected':
        case 'unselected':
            let matching: boolean;
            let captionText: string;
            let descText: string;
            if (step === 'selected') {
                matching = false;
                captionText = selectedCaption || '';
                descText = selectedDesc || '';
            } else {
                matching = true;
                captionText = unselectedCaption || '';
                descText = unselectedDesc || '';
            }

            const cardsToShow = cards.filter((card, index) => {
                return cardsSwipedUp[index] === matching;
            });
            const cardElements = cardsToShow.map((cardToShow, index) => <ApproachAvoidCard
                key={index}
                term={cardToShow.text}
                desc={cardToShow.description}
                imageId={cardToShow.imageId}
            />);
            content = <>
                <h1>{captionText}</h1>
                <p>
                    <TextWithReferences text={descText} />
                </p>
                {cardElements}
            </>;
            break;

        default:
            content = <div>laden...</div>;
    }
    return (
        <div className="game approach-avoid swiper-no-swiping">
            {content}
        </div>
    );
};

export default ApproachAvoid;